import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { usersApi } from '../../api/users';
import './Tutorial.css';

const Tutorial = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [user, setUser] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [elementRect, setElementRect] = useState(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
  const tooltipRef = useRef(null);

  useEffect(() => {
    const currentUser = authApi.getCurrentUser();
    setUser(currentUser);
    
    // Always show tutorial when on tutorial route - INSTANT
    if (location.pathname === '/onboarding/tutorial') {
      // Use requestAnimationFrame for instant display
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
    }
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (!tooltipRef.current || !isVisible) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    if (rect.width && rect.height) {
      setTooltipSize({ width: rect.width, height: rect.height });
    }
  }, [currentStep, isVisible, elementRect]);

  const tutorialSteps = [
    {
      id: 'navbar-logo',
      title: 'Logo DIOS10',
      description: 'Questo è il logo della piattaforma. Cliccandoci puoi tornare sempre alla Home.',
      selector: '[data-tutorial="logo"]',
      position: 'bottom',
    },
    {
      id: 'game-mode-marketplace',
      title: 'Marketplace',
      description: 'Nel Marketplace puoi comprare, vendere e scambiare card con altri giocatori. Puoi anche partecipare ad aste.',
      selector: '[data-tutorial="nav-marketplace"]',
      position: 'bottom',
    },
    {
      id: 'game-mode-tournaments',
      title: 'Tournaments',
      description: 'I Tornei sono il cuore della piattaforma! Partecipa ai GameWeeks, crea la tua formazione e competi per vincere premi.',
      selector: '[data-tutorial="nav-tournaments"]',
      position: 'bottom',
    },
    {
      id: 'game-mode-rankings',
      title: 'Rankings',
      description: 'Consulta le classifiche dei migliori giocatori e le tue posizioni nei vari tornei.',
      selector: '[data-tutorial="nav-rankings"]',
      position: 'bottom',
    },
    {
      id: 'active-gameweeks',
      title: 'GameWeeks Attivi',
      description: 'Qui vedi tutti i GameWeeks attualmente disponibili. Clicca su un GameWeek per vedere i dettagli e partecipare.',
      selector: '[data-tutorial="tournaments"]',
      position: 'top',
    },
    {
      id: 'navbar-deposit',
      title: 'Deposit',
      description: 'Deposita fondi per acquistare pack, partecipare ai tornei o comprare card sul marketplace.',
      selector: '[data-tutorial="nav-deposit"]',
      position: 'bottom',
    },
    {
      id: 'navbar-profile',
      title: 'Profile',
      description: 'Nel tuo profilo puoi vedere tutte le tue card nella sezione "My Cards", gestire i referral e le impostazioni.',
      selector: '[data-tutorial="nav-profile"]',
      position: 'bottom',
    }
  ];

  const scrollToShowBoth = useCallback(() => {
    if (!elementRect || !tooltipRef.current) return;

    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    const currentStepData = tutorialSteps[currentStep];
    
    // Get tooltip dimensions
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRect.height || tooltipSize.height || 200;
    const tooltipDocTop = tooltipRect.top + scrollY;
    const tooltipDocBottom = tooltipRect.bottom + scrollY;
    
    // Calculate tooltip position in document coordinates
    let tooltipTop, tooltipBottom;
    const gap = 12;
    if (currentStepData?.position === 'top') {
      tooltipTop = elementRect.docTop - tooltipHeight - gap;
      tooltipBottom = elementRect.docTop - gap;
    } else {
      tooltipTop = elementRect.docBottom + gap;
      tooltipBottom = elementRect.docBottom + gap + tooltipHeight;
    }

    // Calculate the visible area needed
    const elementTop = elementRect.docTop;
    const elementBottom = elementRect.docBottom;
    const minVisibleTop = Math.min(elementTop, tooltipTop, tooltipDocTop);
    const maxVisibleBottom = Math.max(elementBottom, tooltipBottom, tooltipDocBottom);

    // Check if both are visible in viewport
    const elementVisible = elementRect.top >= 0 && elementRect.bottom <= viewportHeight;
    const tooltipVisible = tooltipRect.top >= 0 && tooltipRect.bottom <= viewportHeight;
    const bothVisible = elementVisible && tooltipVisible;

    if (!bothVisible) {
      // Calculate optimal scroll position to show both
      const neededHeight = maxVisibleBottom - minVisibleTop;
        const padding = 60; // Extra padding for better visibility
      
      if (neededHeight <= viewportHeight - padding) {
        // Both can fit, center them
        const targetScroll = minVisibleTop - (viewportHeight - neededHeight) / 2;
        window.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth'
        });
      } else {
        // Too large, prioritize showing the element and tooltip together
        if (currentStepData?.position === 'top') {
          // Element below, tooltip above - show tooltip at top with padding
          const targetScroll = Math.max(0, tooltipDocTop - padding);
          window.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          });
        } else {
          // Element above, tooltip below - show element at top with padding
          const targetScroll = Math.max(0, elementTop - padding);
          window.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          });
        }
      }
    }
  }, [currentStep, elementRect, tutorialSteps]);

  const highlightElement = useCallback((selector) => {
    // Remove previous highlights
    document.querySelectorAll('[data-tutorial-highlight]').forEach(el => {
      el.removeAttribute('data-tutorial-highlight');
    });

    // Add highlight to current element
    const element = document.querySelector(selector);
    if (element) {
      element.setAttribute('data-tutorial-highlight', 'true');
    }
  }, []);

  const updateElementRect = useCallback(() => {
    if (!isVisible || currentStep >= tutorialSteps.length) {
      setElementRect(null);
      return;
    }

    const step = tutorialSteps[currentStep];
    const element = document.querySelector(step?.selector);
    
    if (element) {
      const rect = element.getBoundingClientRect();
      setElementRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right,
        docTop: rect.top + window.scrollY,
        docLeft: rect.left + window.scrollX,
        docBottom: rect.bottom + window.scrollY,
        docRight: rect.right + window.scrollX,
      });
    } else {
      setElementRect(null);
    }
  }, [currentStep, isVisible, tutorialSteps]);

  useEffect(() => {
    if (!isVisible || currentStep >= tutorialSteps.length || location.pathname !== '/onboarding/tutorial') {
      return;
    }

    const step = tutorialSteps[currentStep];
    
    // INSTANT - check immediately and retry if needed
    const checkAndHighlight = () => {
      const element = document.querySelector(step.selector);
      if (element) {
        highlightElement(step.selector);
        updateElementRect();
        
        // Wait for tooltip to render and element rect to update, then scroll
        setTimeout(() => {
          scrollToShowBoth();
        }, 400);
      } else {
        // Quick retry if element not found yet
        requestAnimationFrame(() => {
          const retryElement = document.querySelector(step.selector);
          if (retryElement) {
            highlightElement(step.selector);
            updateElementRect();
            
            setTimeout(() => {
              scrollToShowBoth();
            }, 400);
          }
        });
      }
    };

    // Use requestAnimationFrame for instant execution
    requestAnimationFrame(checkAndHighlight);

    // Update on scroll/resize - CRITICAL for alignment
    const handleScroll = () => {
      updateElementRect();
      // Small delay to let elementRect update
      setTimeout(() => {
        scrollToShowBoth();
      }, 50);
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', () => {
      updateElementRect();
      setTimeout(() => {
        scrollToShowBoth();
      }, 100);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updateElementRect);
    };
  }, [currentStep, isVisible, location.pathname, highlightElement, updateElementRect, tutorialSteps, scrollToShowBoth]);

  const handleNext = useCallback(async () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Mark tutorial as completed
      if (user?.id) {
        try {
          await usersApi.updateTutorialCompleted(user.id, true);
          const updatedUser = { ...user, tutorial_completed: true };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } catch (error) {
          console.error('Error updating tutorial completion:', error);
        }
      }
      navigate('/onboarding/pack-opening');
    }
  }, [currentStep, user, navigate]);

  const handleSkip = useCallback(async () => {
    // Mark tutorial as completed even if skipped
    if (user?.id) {
      try {
        await usersApi.updateTutorialCompleted(user.id, true);
        const updatedUser = { ...user, tutorial_completed: true };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (error) {
        console.error('Error updating tutorial completion:', error);
      }
    }
    navigate('/onboarding/pack-opening');
  }, [user, navigate]);

  // Auto-scroll when elementRect changes
  useEffect(() => {
    if (elementRect && tooltipRef.current) {
      const timeoutId = setTimeout(() => {
        scrollToShowBoth();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [elementRect, scrollToShowBoth]);

  if (!isVisible || currentStep >= tutorialSteps.length) {
    return null;
  }

  const currentStepData = tutorialSteps[currentStep];

  // Calculate overlay masks (everything except the highlighted element)
  const overlayMasks = elementRect ? [
    // Top mask
    {
      top: 0,
      left: 0,
      width: '100%',
      height: `${elementRect.top}px`,
    },
    // Bottom mask
    {
      top: `${elementRect.bottom}px`,
      left: 0,
      width: '100%',
      height: `calc(100vh - ${elementRect.bottom}px)`,
    },
    // Left mask
    {
      top: `${elementRect.top}px`,
      left: 0,
      width: `${elementRect.left}px`,
      height: `${elementRect.height}px`,
    },
    // Right mask
    {
      top: `${elementRect.top}px`,
      left: `${elementRect.right}px`,
      width: `calc(100% - ${elementRect.right}px)`,
      height: `${elementRect.height}px`,
    },
  ] : [];

  return (
    <>
      {/* Overlay masks - only show dark overlay outside the highlighted element */}
      {elementRect && overlayMasks.map((mask, index) => (
        <div
          key={index}
          className="tutorial-overlay-mask"
          style={mask}
          onClick={handleNext}
        ></div>
      ))}
      
      {/* Spotlight border around element */}
      {elementRect && (
        <div
          className="tutorial-spotlight"
          style={{
            top: `${elementRect.top}px`,
            left: `${elementRect.left}px`,
            width: `${elementRect.width}px`,
            height: `${elementRect.height}px`,
          }}
        ></div>
      )}

      {/* Tooltip */}
      {currentStepData && elementRect && (() => {
        // Calculate tooltip position with bounds checking
        let tooltipTop, tooltipLeft, tooltipTransform;
        const padding = 16;
        const gap = 12;
        const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
        const tooltipHeight = tooltipSize.height || 200;
        const tooltipWidth = tooltipSize.width || 350;
        
        if (currentStepData.position === 'top') {
          // Position above element, but ensure it's visible
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          const topPosition = elementRect.top - tooltipHeight - gap;
          
          // If tooltip would go off top, position it below instead
          const finalTop = topPosition < padding ? (elementRect.bottom + gap) : topPosition;
          tooltipTop = `${clamp(finalTop, padding, viewportHeight - tooltipHeight - padding)}px`;
          tooltipTransform = 'translateX(-50%)';
          
          // Center horizontally, but ensure it stays on screen
          let leftPosition = elementRect.left + elementRect.width / 2;
          leftPosition = clamp(leftPosition, tooltipWidth / 2 + padding, viewportWidth - tooltipWidth / 2 - padding);
          tooltipLeft = `${leftPosition}px`;
        } else if (currentStepData.position === 'bottom-left') {
          // For bottom-left, ensure tooltip stays on screen
          const bottomPosition = elementRect.bottom + gap;
          const leftPosition = elementRect.left;
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          
          // If tooltip would go off bottom, position it above instead
          const finalTop = (bottomPosition + tooltipHeight > viewportHeight - padding)
            ? (elementRect.top - tooltipHeight - gap)
            : bottomPosition;
          tooltipTop = `${clamp(finalTop, padding, viewportHeight - tooltipHeight - padding)}px`;
          tooltipTransform = 'translateX(0)';
          
          // Ensure tooltip doesn't go off left edge
          const safeLeft = clamp(leftPosition, padding, viewportWidth - tooltipWidth - padding);
          tooltipLeft = `${safeLeft}px`;
        } else if (currentStepData.position === 'bottom') {
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          const bottomPosition = elementRect.bottom + gap;
          const finalTop = (bottomPosition + tooltipHeight > viewportHeight - padding)
            ? (elementRect.top - tooltipHeight - gap)
            : bottomPosition;
          tooltipTop = `${clamp(finalTop, padding, viewportHeight - tooltipHeight - padding)}px`;
          const leftPosition = elementRect.left + elementRect.width / 2;
          tooltipLeft = `${clamp(leftPosition, tooltipWidth / 2 + padding, viewportWidth - tooltipWidth / 2 - padding)}px`;
          tooltipTransform = 'translateX(-50%)';
        } else {
          // Right position
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          const topPosition = elementRect.top + elementRect.height / 2 - tooltipHeight / 2;
          tooltipTop = `${clamp(topPosition, padding, viewportHeight - tooltipHeight - padding)}px`;
          let leftPosition = elementRect.right + gap;
          if (leftPosition + tooltipWidth > viewportWidth - padding) {
            leftPosition = elementRect.left - tooltipWidth - gap;
          }
          tooltipLeft = `${clamp(leftPosition, padding, viewportWidth - tooltipWidth - padding)}px`;
          tooltipTransform = 'translateX(0)';
        }
        
        return (
          <div
            ref={tooltipRef}
            className={`tutorial-tooltip tutorial-tooltip-${currentStepData.position}`}
            style={{
              top: tooltipTop,
              left: tooltipLeft,
              transform: tooltipTransform,
            }}
          >
          <div className="tutorial-tooltip-content">
            <div className="tutorial-progress">
              <span className="tutorial-step-number">{currentStep + 1} / {tutorialSteps.length}</span>
            </div>
            <h3 className="tutorial-title">{currentStepData.title}</h3>
            <p className="tutorial-description">{currentStepData.description}</p>
            <div className="tutorial-actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSkip();
                }}
                className="tutorial-btn-skip"
              >
                Salta Tutorial
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className="tutorial-btn-next"
              >
                {currentStep === tutorialSteps.length - 1 ? 'Continua' : 'Avanti'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </>
  );
};

export default Tutorial;
